import React from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { IgniteIcon } from '@kar-dmp/pattern-library/react';
import { RelativeSize, Icon, CSSColorKeyword } from '@kar-dmp/pattern-library';
import ReducerConstants from '../../../../_shared/constants/reducers';
import Loading from '../../../../_shared/components/Loading';
import convertToCurrency from '../../../../_shared/utils/convertToCurrency';
import { BidStatus } from '../../../../_shared/types/auction';

import {
  ListVariant,
  TradeContext,
  TradeState,
  TradeViewVariant
} from '../../../../_shared/types/trade';
import renderLocale from '../../../../_shared/helpers/renderLocale';

import PendingBidBoxFooterContainer from './PendingBidBoxFooterContainer';

import {
  StyledBidButton,
  StyledControls,
  StyledQuickBidButton,
  StyledQuickBidButtonText
} from './Styles';
import useFeatureFlags from '../../../../_shared/hooks/useFeatureFlags';
import FEATURE_FLAGS from '../../../../_shared/constants/featureFlags';
import isOmpPlatform from '../../../../_shared/utils/isOmpPlatform';

const propTypes = {
  bidAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bidIncrements: PropTypes.number,
  bidStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentProxyAmount: PropTypes.number,
  currentBidAmount: PropTypes.number,
  formSubmitting: PropTypes.bool.isRequired,
  isAuctionEnded: PropTypes.bool,
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  isSettingProxy: PropTypes.bool,
  mediaCounts: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  hasVehicleHistoryReport: PropTypes.bool,
  numberOfBids: PropTypes.number,
  proxyAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tradeState: PropTypes.number,
  variant: PropTypes.string,
  tradeId: PropTypes.number,
  onBidButtonClick: PropTypes.func.isRequired,
  onQuickBidButtonClick: PropTypes.func.isRequired,
  onProxyButtonClick: PropTypes.func.isRequired,
  isSelling: PropTypes.bool,
  minBidAmount: PropTypes.number
};

const defaultProps = {
  bidAmount: undefined,
  bidIncrements: undefined,
  bidStatus: undefined,
  currentBidAmount: undefined,
  currentProxyAmount: undefined,
  isAuctionEnded: undefined,
  isLoading: undefined,
  isSettingProxy: false,
  isEditing: undefined,
  mediaCounts: undefined,
  hasVehicleHistoryReport: undefined,
  numberOfBids: undefined,
  proxyAmount: undefined,
  tradeState: undefined,
  variant: undefined,
  tradeId: undefined,
  isSelling: false,
  minBidAmount: undefined
};

const Controls = ({
  bidAmount,
  bidIncrements,
  bidStatus,
  currentBidAmount,
  currentProxyAmount,
  formSubmitting,
  hasVehicleHistoryReport,
  isAuctionEnded,
  isEditing,
  isLoading,
  isSelling,
  isSettingProxy,
  mediaCounts,
  minBidAmount,
  numberOfBids,
  onBidButtonClick,
  onProxyButtonClick,
  onQuickBidButtonClick,
  proxyAmount,
  tradeId,
  tradeState,
  variant
}) => {
  const [user] = useSelector(
    state => [state[ReducerConstants.AUTH]?.user],
    shallowEqual
  );
  const userLocale = renderLocale(user?.auth);
  const localeVariant = userLocale ? userLocale.substring(0, 2) : null;
  const tradeContext = isSelling ? TradeContext.Sell : TradeContext.Buy;
  const isQuickBidEnabled =
    useFeatureFlags(FEATURE_FLAGS.IS_MIN_BID_QUICK_BID_ENABLED) || false;
  const isMinBid =
    isQuickBidEnabled &&
    tradeState === TradeState.active &&
    !isAuctionEnded &&
    numberOfBids === 0;
  const isOmp = isOmpPlatform();
  const isQuickBid =
    (tradeState === TradeState.active || tradeState === TradeState.closing) &&
    !isAuctionEnded &&
    numberOfBids > 0 &&
    bidIncrements;

  const getQuickBidButtonText = () => {
    return isMinBid ? (
      <StyledQuickBidButtonText isOmpStyles={isOmp}>
        <FormattedMessage id="vdp.bid.button.quick.min.bid" />
        <br />
        {convertToCurrency(minBidAmount)}
      </StyledQuickBidButtonText>
    ) : (
      <StyledQuickBidButtonText isOmpStyles={isOmp}>
        +{convertToCurrency(bidIncrements)}
      </StyledQuickBidButtonText>
    );
  };

  /**
   * Components
   */
  const BidButton = () =>
    !isAuctionEnded &&
    (tradeState === TradeState.active || tradeState === TradeState.closing) && (
      <StyledBidButton
        className="notranslate bid-button"
        id="vdp-bidbutton"
        data-testid={`bid-button${
          variant === TradeViewVariant.LIST && tradeId
            ? '-'.concat(tradeId, '-list')
            : ''
        }`}
        onClick={e => {
          onBidButtonClick();
          e.stopPropagation();
        }}
        disabled={isAuctionEnded || formSubmitting}
        variant={variant}
        tradeState={tradeState}
        isOmpStyles={isOmp}
      >
        {/* BID */}
        {bidAmount ? (
          <>
            <FormattedMessage id="vdp.bid.button.bid" />
            &nbsp;
            {convertToCurrency(bidAmount)}
          </>
        ) : (
          <FormattedMessage id="vdp.bid.button.placeBid" />
        )}
      </StyledBidButton>
    );

  const QuickBidButton = () =>
    (isQuickBid || isMinBid) && (
      <StyledQuickBidButton
        className="notranslate bid-button"
        testId={`bid-increment${
          variant === TradeViewVariant.LIST && tradeId
            ? '-'.concat(tradeId, '-list')
            : ''
        }`}
        disabled={isAuctionEnded || formSubmitting}
        onClick={e => {
          onQuickBidButtonClick(isMinBid ? minBidAmount : bidIncrements);
          e.stopPropagation();
        }}
        variant={variant}
        isOmpStyles={isOmp}
      >
        {isOmp && (
          <IgniteIcon
            icon={Icon.LightningBolt}
            color={CSSColorKeyword.CurrentColor}
            size={RelativeSize.Medium}
          />
        )}
        {getQuickBidButtonText()}
      </StyledQuickBidButton>
    );

  const proxyAndBidButtonModal =
    isEditing && bidAmount ? (
      <FormattedMessage
        id="vdp.bid.button.setProxyAndBidAmount"
        values={{
          proxyAmount: convertToCurrency(proxyAmount),
          bidAmount: convertToCurrency(bidAmount)
        }}
        className="notranslate"
      />
    ) : (
      <FormattedMessage
        id="vdp.bid.button.setProxyAmount"
        values={{ proxyAmount: convertToCurrency(proxyAmount) }}
      />
    );

  const proxyButtonModal = proxyAmount ? (
    proxyAndBidButtonModal
  ) : (
    <FormattedMessage id="vdp.bid.button.setProxy" />
  );

  const getEditProxyButtonText = () =>
    variant === ListVariant.GRID ? (
      <FormattedMessage id="vdp.bid.button.editProxy" />
    ) : (
      <FormattedMessage
        id="vdp.bid.button.editProxyAmount"
        values={{
          proxyAmount: convertToCurrency(currentProxyAmount)
        }}
        className="notranslate"
      />
    );

  const proxyButtonBidBox = currentProxyAmount ? (
    getEditProxyButtonText()
  ) : (
    <FormattedMessage id="vdp.bid.button.setProxy" />
  );

  const onProxyClick = e => {
    if (tradeState === TradeState.active) {
      onBidButtonClick();
    } else {
      onProxyButtonClick();
    }

    e.stopPropagation();
  };

  const ProxyButton = () => (
    <StyledBidButton
      id="vdp-bidbutton"
      className="notranslate"
      data-testid={`proxy-bid-button${
        variant === TradeViewVariant.LIST && tradeId
          ? '-'.concat(tradeId, '-list')
          : ''
      }`}
      disabled={
        (tradeState === TradeState.upcoming &&
          ((mediaCounts && !mediaCounts.vehicleHistoryReportUrl) ||
            (!mediaCounts && !hasVehicleHistoryReport))) ||
        formSubmitting
      }
      onClick={onProxyClick}
      locale={localeVariant}
      variant={variant}
      type="proxy"
      tradeState={tradeState}
      isOmpStyles={isOmp}
    >
      {isSettingProxy ? proxyButtonModal : proxyButtonBidBox}
    </StyledBidButton>
  );

  const ActiveStateBiddingButtons = () => {
    if (isAuctionEnded) {
      return null;
    }
    if (
      isSettingProxy ||
      (currentProxyAmount &&
        bidStatus !== BidStatus.outbid &&
        currentProxyAmount !== currentBidAmount)
    ) {
      return <ProxyButton />;
    }

    return (
      <>
        <BidButton />
        <QuickBidButton />
      </>
    );
  };

  const BiddingButtonsBuyer = () => (
    <StyledControls
      variant={variant}
      tradeState={tradeState}
      isOmpStyles={isOmp}
    >
      {(() => {
        switch (tradeState) {
          case TradeState.upcoming:
            return <ProxyButton variant={variant} />;
          case TradeState.closing:
            return (
              <>
                <BidButton />
                <QuickBidButton />
              </>
            );
          case TradeState.active:
            return <ActiveStateBiddingButtons />;
          default:
            return null;
        }
      })()}
    </StyledControls>
  );

  if (tradeContext === TradeContext.Sell && tradeState !== TradeState.pending) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledControls
        variant={variant}
        tradeState={tradeState}
        isOmpStyles={isOmp}
      >
        <Loading width="100%" height="48px" />
      </StyledControls>
    );
  }

  if (tradeState === TradeState.pending) {
    return (
      <PendingBidBoxFooterContainer isOmp={isOmp} tradeContext={tradeContext} />
    );
  }

  return <BiddingButtonsBuyer />;
};

Controls.propTypes = propTypes;
Controls.defaultProps = defaultProps;

export default Controls;
